import React from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import type { TicketDocumentsTabProps } from './types';

const TicketDocumentsTab: React.FC<TicketDocumentsTabProps> = ({
  documents,
  loadingDocs,
  onUnlinkDocument,
  onLinkDocument,
  onDownloadDocument,
  onNavigateToDocument,
  allDocuments,
  showAddDocument,
  onShowAddDocument,
  onHideAddDocument,
}) => {
  if (loadingDocs) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Verknüpfte Dokumente</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={onShowAddDocument}
        >
          Dokument hinzufügen
        </Button>
      </Box>

      {documents.length === 0 ? (
        <Alert severity="info">
          Keine Dokumente mit diesem Ticket verknüpft.
        </Alert>
      ) : (
        <List>
          {documents.map((doc) => (
            <ListItem key={doc.dokumentID}>
              <AttachFileIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <ListItemText
                primary={doc.betreff}
                secondary={
                  <>
                    <Chip label={doc.dokumentTyp} size="small" sx={{ mr: 1 }} />
                    <Chip label={doc.status} size="small" sx={{ mr: 1 }} />
                    {new Date(doc.erstelltAm).toLocaleDateString('de-DE')}
                  </>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  onClick={() => onDownloadDocument(doc.dokumentID)}
                  title="Herunterladen"
                  size="small"
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  onClick={() => onNavigateToDocument(doc.dokumentID)}
                  title="Dokument bearbeiten"
                  size="small"
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => onUnlinkDocument(doc.dokumentID)}
                  title="Verknüpfung entfernen"
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {showAddDocument && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Dokument auswählen:
          </Typography>
          <Autocomplete
            options={allDocuments.filter(
              (doc) => !documents.some((d) => d.dokumentID === doc.dokumentID)
            )}
            getOptionLabel={(option) => option.betreff}
            renderInput={(params) => (
              <TextField {...params} label="Dokument" placeholder="Suchen..." />
            )}
            onChange={(_, value) => {
              if (value) {
                onLinkDocument(value.dokumentID);
              }
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" onClick={onHideAddDocument}>
              Abbrechen
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TicketDocumentsTab;
